<script setup>
import { ref } from "vue";
import dayjs from "dayjs";
import Rating from "@/components/Rating.vue";
import SearchableModel from "@/components/searchable-model/SearchableModel.vue";
import { useBulkActionApi } from "@/composables/useBulkActionApi";
import useItemSelection from "@/composables/useItemSelection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/shadcn/components/ui/alert-dialog/index.js";
import {
  EyeIcon,
  EyeOffIcon,
  ArchiveIcon,
  ArchiveRestoreIcon,
  Trash2Icon,
  ChevronDownIcon,
  PenIcon,
  CopyIcon,
} from "@lucide/vue";
import { Button } from "@/shadcn/components/ui/button/index.js";
import { toast } from "vue-sonner";
import Toggle from "@/components/ui/Toggle.vue";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/shadcn/components/ui/dropdown-menu/index.js";
import { Badge } from "@/components/ui/badge/index.js";

const props = defineProps({
  baseFilters: {
    type: Object,
    default: () => ({})
  },
  availableFilters: {
    type: Array,
    default: () => ([])
  },
  pushState: {
    type: Boolean,
    default: false
  },
  withProductActions: {
    type: Boolean,
    default: false
  },
  withExport: {
    type: Boolean,
    default: false
  },
  bulkActionUrl: {
    type: String,
    default: undefined
  },
});

const searchableModelRef = ref(null);
const {
  selectedIds,
  isSelected,
  select,
  deselect,
  deselectAll,
  updateAllSelection,
  selectedCount,
} = useItemSelection();
const productIdsToArchive = ref([]);

const showBulkConfirmation = ref(false);

const bulkAction = ref({
  label: '',
  action: '',
  parameters: {}
});

const onClickBulkAction = (label, action, parameters = {}, productId) => {
  if (productId) {
    deselectAll();
    select(productId);
  }
  if (selectedIds.value.length === 0) {
    return;
  }
  showBulkConfirmation.value = true;
  bulkAction.value.label = label;
  bulkAction.value.action = action;
  bulkAction.value.parameters = parameters;
};

const setBulkActionParameter = (parameter, value) => {
  if (!bulkAction.value.parameters) {
    bulkAction.value.parameters = {};
  }
  bulkAction.value.parameters[parameter] = value;
};


const { loading: waitingBulkResult, execute: executeBulkAction } = useBulkActionApi(props.bulkActionUrl);
const onConfirmBulkAction = async () => {

  if (waitingBulkResult.value) {
    return;
  }
  const response = await executeBulkAction(bulkAction.value.action, selectedIds.value, bulkAction.value.parameters);
  if (response.errors) {
    response.errors.forEach(error => {
      toast.error(error)
    })
  }
  showBulkConfirmation.value = false;
  deselectAll();
  await searchableModelRef.value.getResults()
};

const onUpdateAllSelection = (models, checked) => {
  updateAllSelection(checked, models);
};

const showArchiveProductsModal = ref(false);

const archiveProducts = (productIds) => {
  productIdsToArchive.value = productIds;
  showArchiveProductsModal.value = true;
};
</script>
<template>
  <div>
    <SearchableModel
      model-name="Product"
      :base-filters="props.baseFilters"
      :available-filters="availableFilters"
      :push-state="pushState"
      :with-export="withExport"
      ref="searchableModelRef"
      >
      <template #header-title v-if="$slots['header-title']">
        <slot name="header-title"></slot>
      </template>

      <template #header-actions v-if="$slots['header-actions']">
        <slot name="header-actions"></slot>
      </template>

      <template #default="{ models }">
        <div class="table-responsive">
          <template v-if="selectedCount > 0">
            <div class="d-print-none tw:flex tw:items-center tw:gap-2 tw:mb-4">
                  <span class="">
                    {{ $t('Bulk actions:') }}
                  </span>
              <Button variant="outline"
                      @click="onClickBulkAction($t('Set public'), 'toggle-public', { public: true })">
                <EyeIcon class="tw:w-3 tw:h-3" /> {{ $t('Set public') }}
              </Button>

              <Button variant="outline"
                      @click="onClickBulkAction($t('Set private'), 'toggle-public', { public: false })">
                <EyeOffIcon class="tw:w-3 tw:h-3" /> {{ $t('Set private') }}
              </Button>

              <Button variant="outline"
                      @click="onClickBulkAction($t('Archive'), 'archive', { 'deleteBarcode' : true })">
                <ArchiveIcon class="tw:w-3 tw:h-3" /> {{ $t('Archive') }}
              </Button>

              <Button variant="outline"
                      @click="onClickBulkAction($t('Restore'), 'restore')">
                <ArchiveRestoreIcon class="tw:w-3 tw:h-3" /> {{ $t('Restore') }}
              </Button>

              <Button variant="ghost-destructive"
                      @click="onClickBulkAction($t('Delete'), 'delete')">
                <Trash2Icon class="tw:w-3 tw:h-3" /> {{ $t('Delete') }}
              </Button>
              <span class="tw:ml-auto tw:text-zinc-500">
                    {{ $t("Models selected", {count: selectedCount}, selectedCount) }}
                  </span>
            </div>
          </template>
          <table class="table">
            <thead class="tw:text-left tw:text-sm tw:bg-zinc-50 tw:hidden tw:md:table-header-group">
            <tr>
              <th v-if="withProductActions" class="d-print-none tw:w-px tw:whitespace-nowrap">
                <input type="checkbox"
                       :checked="selectedCount === models?.length"
                       :indeterminate="selectedCount > 0 && selectedCount < models?.length"
                       @change="onUpdateAllSelection(models, $event.target.checked)"/>
              </th>
              <th class="tw:text-left"></th>
              <th class="tw:text-left">{{ $t('Model name') }}</th>
              <th class="tw:text-left">{{ $t('Year') }}</th>
              <th class="tw:text-left">{{ $t('Visibility') }}</th>
              <th class="tw:text-left">{{ $t('Stock') }}</th>
              <th class="tw:text-left">{{ $t('Tests') }}</th>
              <th class="tw:text-left">{{ $t('Reviews') }}</th>
              <th class="tw:text-left" v-if="withProductActions">{{ $t('Actions') }}</th>
            </tr>
            </thead>
            <tr v-for="product in models" :key="product.id" :class="{'tw:bg-amber-50': isSelected(product.id)}">
              <td v-if="withProductActions" class="d-print-none tw:w-px tw:whitespace-nowrap">
                <input type="checkbox"
                       :checked="isSelected(product.id)"
                       @change="isSelected(product.id) ? deselect(product.id) : select(product.id)">
              </td>
              <td>
                <a :href="product.links.show" style="width: 50px">
                  <img :src="product.image" alt="" width="50">
                </a>
              </td>
              <td>
                <a :href="product.links.show">
                  <strong>{{ product.name }}</strong>
                  <span v-if="product.reference" class="ml-1">({{ product.reference }})</span>
                </a>
                <br>
                <span class="text-muted">
                  {{ product.category }}
                </span>
              </td>
              <td>
                {{ product.year }}
              </td>
              <td>
                <template v-if="product.is_archived">
                  <Badge variant="red">
                    <ArchiveIcon class="tw:w-3 tw:h-3 tw:stroke-current" />
                    <span>{{ $t('Archived') }}</span>
                  </Badge>
                </template>
                <template v-else-if="product.public">
                  <Badge variant="green">
                    <EyeIcon class="tw:w-3 tw:h-3 tw:stroke-current" />
                    <span>{{ $t('Public') }}</span>
                  </Badge>
                </template>
                <template v-else>
                  <Badge variant="zinc">
                    <EyeOffIcon class="tw:w-3 tw:h-3 tw:stroke-current"/>
                    <span>{{ $t('Private') }}</span>
                  </Badge>
                </template>
              </td>

              <td>
                <a :href="product.links.equipments" :class="[
                  product.equipments_count === 0 ? 'text-danger' : '',
                ]">
                  {{ $t('items_count', { count: product.equipments_count}, product.equipments_count) }}
                </a>
              </td>
              <td>
                {{ product.product_tests_count }}
                <template v-if="product?.last_product_test_end_at">
                  <br>
                  <small class="text-muted">{{ dayjs(product.last_product_test_end_at).fromNow() }}</small>
                </template>
              </td>
              <td>
                <span v-if="product.overall_rating">
                  <small class="text-muted tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                    <Rating :rating-value="product.overall_rating"></Rating>
                    ({{ product.overall_rating }})
                  </small>
                  <small>
                    <a :href="product.links.reviews">
                      {{ $t('reviews_nbr', {count: product.number_of_reviews}, product.number_of_reviews) }}
                    </a>
                  </small>
                </span>
              </td>
              <td v-if="withProductActions" class="tw:whitespace-nowrap tw:text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button variant="outline">
                      {{ $t('Actions') }}
                      <ChevronDownIcon class="tw:ml-2 tw:h-4 tw:w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem v-if="product.links?.edit" as-child>
                      <a :href="product.links.edit">
                        <PenIcon class="tw:w-4 tw:h-4" />
                        {{ $t('Edit') }}
                      </a>
                    </DropdownMenuItem>
                    <template v-if="product.links?.copy">
                      <DropdownMenuItem as-child>
                        <a :href="product.links.copy">
                          <CopyIcon class="tw:size-4" />
                          {{ $t('Copy') }}
                        </a>
                      </DropdownMenuItem>
                    </template>
                    <DropdownMenuSeparator />
                    <template v-if="!product.is_archived">
                      <DropdownMenuItem
                        @click="onClickBulkAction($t(product.public ? 'Set private' : 'Set public'), 'toggle-public', { public: !product.public }, product.id)">
                        <EyeOffIcon v-if="product.public" class="tw:w-4 tw:h-4" />
                        <EyeIcon v-else class="tw:w-4 tw:h-4" />
                        {{ product.public ? $t('Set private') : $t('Set public') }}
                      </DropdownMenuItem>
                    </template>
                    <DropdownMenuItem
                      @click="onClickBulkAction($t('Archive'), product.is_archived ? 'restore' : 'archive', { 'deleteBarcode' : true }, product.id)">
                        <ArchiveRestoreIcon v-if="product.is_archived"  class="tw:w-4 tw:h-4" />
                        <ArchiveIcon v-else class="tw:w-4 tw:h-4" />
                        {{ product.is_archived ? $t('Restore') : $t('Archive') }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          </table>
        </div>
      </template>
    </SearchableModel>

    <AlertDialog :open="showBulkConfirmation" @update:open="showBulkConfirmation = $event">
      <AlertDialogContent class="tw:!max-w-sm tw:gap-6">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {{ bulkAction.label }}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {{ $t('Are you sure you want to perform this bulk action on the selected models?') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <template v-if="bulkAction.action === 'archive'">
          <div class="tw:flex tw:items-center tw:gap-2">
            <Toggle id="frm-bulk-action-delete-barcodes"
                    :model-value="bulkAction.parameters.deleteBarcode"
                    @update:modelValue="setBulkActionParameter('deleteBarcode', $event)"/>
            <label for="frm-bulk-action-delete-barcodes" class="tw:text-sm">
              {{ $t('Delete associated items') }}
            </label>
          </div>
        </template>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="secondary" @click="showBulkConfirmation = false" :disabled="waitingBulkResult">{{ $t('Cancel') }}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild :variant="bulkAction.action === 'delete' ? 'destructive' : 'primary'">
            <Button @click="onConfirmBulkAction()"
                    :loading="waitingBulkResult">{{ $t('Confirm') }}</Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<style scoped>
td {
  vertical-align: middle;
}
</style>
